import { LitElement, html, PropertyValues } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import componentStyles from './idb-aurora-chat.css.lit';
import { getIconChat, getIconAurora, getIconMaximun, getIconSendChat } from './idb-aurora-icons/idb-aurora-icons';
import { ChatStorageService } from './data/ChatStorageService';

@customElement('idb-aurora-chat')
export class ChatBot extends LitElement {

  @property({ type: Boolean, attribute: 'is-expanded' })
  isExpanded: boolean = false;

  @property({ type: String }) apiEndpoint = '';
  @property({ type: String }) title = '';
  @property({ type: String }) legend = '';
  @property({ type: String }) link = '';
  @property({ type: String }) initialMessage = '';

  @state() private isOpen = false;
  @state() private isMaximized = false;
  @state() private isProcessing = false;
  @state() private inputMessage: string = '';
  @state() charCount = 0;
  @state() private messages: Array<{ text: string, isUser: boolean }> = [];
  @state() private previousScrollPosition = 0;
  @state() private previousContainerHeight = 0;

  @state() private buttonStyle: { position: string; bottom: string; window: string } = {
    position: 'fixed',
    bottom: '20px',
    window: '80px'
  };

  private _scrollListener: (() => void) | null = null;
  private readonly maxLength = 500;

  static styles = [componentStyles];

  constructor() {
    super();
    this.messages = ChatStorageService.getMessages();
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    requestAnimationFrame(() => {
      const messagesContainer = this.shadowRoot?.querySelector('.aurora-chat-messages');
      if (messagesContainer && changedProperties.has('messages')) {
        const newScrollHeight = messagesContainer.scrollHeight;
        const heightDifference = newScrollHeight - this.previousContainerHeight;
        messagesContainer.scrollTop = this.previousScrollPosition + heightDifference;
      }
    });

    if (changedProperties.has('initialMessage') && this.initialMessage) {
      if (this.messages.length === 0) {
        this.messages = [{ text: this.initialMessage, isUser: false }];
      }
    }
  }

  private saveMessages() {
    const history = this.messages.slice(-20);
    localStorage.setItem('idb-aurora-chat', JSON.stringify(history));
  }

  private setupScrollObserver() {
    let lastScrollTop = 0;
    let footerObserver: IntersectionObserver;

    const handleScroll = () => {
      const footer = document.querySelector('footer');
      if (!footer) return;

      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

      if (currentScroll < 50) {
        this.buttonStyle = {
          position: 'fixed',
          bottom: '20px',
          window: '80px'
        };
        this.requestUpdate();
        return;
      }

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const isScrollingUp = currentScroll < lastScrollTop;
      lastScrollTop = currentScroll;

      const distanceFromFooter = windowHeight - footerRect.top;

      if (footerRect.top <= windowHeight + 100) {
        const bottomOffset = Math.max(20, distanceFromFooter + 20);
        const windowOffset = Math.max(80, distanceFromFooter + 80);

        this.buttonStyle = {
          position: 'fixed',
          bottom: `${bottomOffset}px`,
          window: `${windowOffset}px`
        };
      } else if (isScrollingUp || currentScroll < 100) {
        this.buttonStyle = {
          position: 'fixed',
          bottom: '20px',
          window: '80px'
        };
      }

      this.requestUpdate();
    };

    footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            handleScroll();
          }
        });
      },
      {
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: '100px'
      }
    );

    const footer = document.querySelector('footer');
    if (footer) {
      footerObserver.observe(footer);
    }

    let ticking = false;
    this._scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', this._scrollListener);
    handleScroll();
  }

  connectedCallback() {
    super.connectedCallback();
    const drupalSettings = (window as any).drupalSettings;

    const savedMessages = ChatStorageService.getMessages();
    if (savedMessages.length > 0) {
      this.messages = savedMessages;
    } else if (this.initialMessage) {
      this.messages = [{ text: this.initialMessage, isUser: false }];
      ChatStorageService.saveMessages(this.messages);
    }

    if (drupalSettings?.idbAuroraChat?.apiUrl) {
      this.apiEndpoint = drupalSettings.idbAuroraChat.apiUrl;
      this.handleSend();
    } else {
      console.error('Not URL (drupalSettings)');
    }

    this.setupScrollObserver();

    window.addEventListener('chatStorageUpdated', () => {
      this.messages = ChatStorageService.getMessages();
    });

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'idb-aurora-chat') {
        if (event.newValue) {
          try {
            this.messages = JSON.parse(event.newValue);
          } catch (error) {
            console.error('Error parsing storage event:', error);
          }
        } else {
          this.messages = [];
        }
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._scrollListener) {
      window.removeEventListener('scroll', this._scrollListener);
      this._scrollListener = null;
    }
  }

  willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties);
    requestAnimationFrame(() => {
      const messagesContainer = this.shadowRoot?.querySelector('.aurora-chat-messages');
      if (messagesContainer) {
        this.previousScrollPosition = messagesContainer.scrollTop;
        this.previousContainerHeight = messagesContainer.scrollHeight;
      }
    });
  }

  private preprocessText(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/^# (.*?)$/gm, "<h1>$1</h1>")
      .replace(/^## (.*?)$/gm, "<h2>$1</h2>")
      .replace(/^### (.*?)$/gm, "<h3>$1</h3>")
      .replace(/^#### (.*?)$/gm, "<h4>$1</h4>")
      .replace(/^##### (.*?)$/gm, "<h5>$1</h5>")
      .replace(/^###### (.*?)$/gm, "<h6>$1</h6>")
      .replace(/^- (.*?)$/gm, "<ul><li>$1</li></ul>")
      .replace(/^(\d+)\. (.*?)$/gm, "<ul><li>$2</li></ul>")
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>')
      .replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1">')
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
      .replace(/^> (.*?)$/gm, "<blockquote>$1</blockquote>")
      .replace(/^---$/gm, "<hr>");
  }

  private toggleChat(event: Event) {
    event.preventDefault();
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.isMaximized = false;
    }
  }

  private toggleMaximize() {
    this.isMaximized = !this.isMaximized;
  }

  private handleInput(e: Event) {
    const target = e.target as HTMLInputElement;
    this.inputMessage = target.value.slice(0, this.maxLength);
    this.charCount = this.inputMessage.length;
  }

  private async handleSend() {
    const message = this.inputMessage.trim();
    if (!message) return;

    this.messages = [...this.messages, { text: message, isUser: true }];
    this.saveMessages();
    this.inputMessage = '';
    this.isProcessing = true;

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message })
      });

      if (!response.ok) throw new Error('Request failed');

      const data = await response.json();
      const botMessage = data.result || 'No Request.';

      this.messages = [...this.messages, { text: botMessage, isUser: false }];
      this.saveMessages();
      this.isProcessing = false;
    } catch (error) {
      this.messages = [
        ...this.messages,
        { text: 'Bad Request Server ChatBot', isUser: false }
      ];
      this.isProcessing = false;
      this.saveMessages();
    }
  }

  render() {
    return html`
      <div id="aurora-chat" class="${this.isOpen ? '' : 'hidden'}">

        <!-- float button -->
        <idb-button
          class="mr-2 float-buttom ${this.isMaximized ? 'hiddenElement' : ''}"
          style="
            position: ${this.buttonStyle.position};
            bottom: ${this.buttonStyle.bottom};
          "
          button-size="medium"
          @click="${(e: Event) => this.toggleChat(e)}"
          rounded="true"
          button-variant="primary"
          icon-only="true"
          button-url=""
        >
          ${unsafeHTML(getIconAurora())}
        </idb-button>

        <!-- window chat -->
        <div
          class="chat-window ${this.isOpen ? 'open' : ''} ${this.isMaximized ? 'maximized' : ''}"
          style="
            position: ${this.buttonStyle.position};
            bottom: ${this.buttonStyle.window};
          "
        >
          <div class="chat-header ${this.isMaximized ? 'maximized' : ''}">
            <div class="chat-header-icon">
              ${unsafeHTML(getIconChat('#004E70'))}
              <span>${this.title}</span>
            </div>

            <div class="chat-header-button">
              <button class="button-max" @click="${this.toggleMaximize}">
                ${unsafeHTML(getIconMaximun())}
              </button>
            </div>
          </div>

          <div class="legend">
            <span>
              <a target="_blank" href=${this.link}>${this.legend}</a>
            </span>
          </div>

          <!-- container message -->
          <div class="chat-body aurora-chat-messages">
            ${this.messages.map(msg => {
              return msg.isUser
                ? html`<div class="sendMessage">${msg.text}</div>`
                : html`
                    <div class="aurora-bot">
                      <span class="icon-bot-message">
                        ${unsafeHTML(getIconChat('#fff'))}
                      </span>
                      <span class="bot-message-content">
                        ${unsafeHTML(this.preprocessText(msg.text))}
                      </span>
                    </div>
                  `;
            })}

            ${this.isProcessing ? html`
              <div class="aurora-bot">
                <span class="icon-bot-message">
                  ${unsafeHTML(getIconChat('#fff'))}
                </span>
                <div class="loading-dots">
                  <div class="dot"></div>
                  <div class="dot"></div>
                  <div class="dot"></div>
                </div>
              </div>
            ` : ''}
          </div>

          <!-- input message -->
          <div class="chat-input aurora-chat-input">
            <input
              type="text"
              maxlength=${this.maxLength}
              placeholder="Type message..."
              @input="${this.handleInput}"
              .value="${this.inputMessage}"
              @keyup="${(e: KeyboardEvent) => e.key === 'Enter' && this.handleSend()}"
              ?disabled="${this.isProcessing}"
            />
            <span class="counter">${this.charCount} / ${this.maxLength}</span>
            <button @click="${this.handleSend}">
              ${unsafeHTML(getIconSendChat('#fff'))}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
